import type { IMediaResponse, IMediaResponseV1, MediaReactions } from "@/types"
import { Album } from "@/models/album"

export enum MediaType {
  Vid = "vid",
  Pic = "pic",
  Unknown = "unknown",
}

export class Media {
  public id?: number
  public albumId?: number
  public uploadedById?: number
  public uploadedBy?: string
  public title?: string
  public description?: string
  public fileName?: string
  public snapshotUrl?: string
  public thumbnailUrl?: string
  public fullSizeUrl?: string
  public createdAt?: Date
  public isPinned?: boolean = false
  public downloadUrl?: string
  public type?: MediaType
  public isPrivileged: boolean = false
  public reactions?: MediaReactions
  public album?: Album
  public tags?: string[]
  public totalComments?: number
  public requiresModeration?: boolean
  public isFlagged?: boolean

  constructor(media: Partial<Media>) {
    this.id = media.id
    this.albumId = media.albumId
    this.uploadedById = media.uploadedById
    this.uploadedBy = media.uploadedBy
    this.title = media.title
    this.description = media.description
    this.fileName = media.fileName
    this.snapshotUrl = media.snapshotUrl
    this.thumbnailUrl = media.thumbnailUrl
    this.fullSizeUrl = media.fullSizeUrl
    this.isPinned = media.isPinned
    this.createdAt = media.createdAt
    this.downloadUrl = media.downloadUrl
    this.type = media.type ?? MediaType.Unknown
    this.isPrivileged = media.isPrivileged ?? false
    this.album = media.album
    this.reactions = media.reactions
    this.tags = media.tags ?? []
    this.totalComments = media.totalComments ?? 0
    this.requiresModeration = media.requiresModeration ?? false
    this.isFlagged = media.isFlagged ?? false
  }

  getThumbnailUrl(): string | undefined {
    switch (this.type) {
      case "vid":
        return this.snapshotUrl
      case "pic":
        return this.thumbnailUrl
      default:
        return this.thumbnailUrl
    }
  }

  public static toRequest(media: Partial<Media>) {
    let type: number = 0

    switch (media.type) {
      case MediaType.Vid:
        type = 1
        break
      case MediaType.Pic:
        type = 0
        break
      default:
        break
    }

    return {
      EventMediaID: media.id,
      EventAlbumID: media.albumId,
      EventGuestID: media.uploadedById,
      GuestName: media.uploadedBy,
      Title: media.title,
      Description: media.description,
      Name: media.fileName,
      PathToSnapshot: media.snapshotUrl,
      PathToThumb: media.thumbnailUrl,
      IsPinned: media.isPinned,
      DateCreated: media.createdAt,
      Type: type,
    }
  }

  public static fromResponseV1(response: IMediaResponseV1): Media {
    let type = MediaType.Unknown

    switch (response.type) {
      case "Photo":
        type = MediaType.Pic
        break
      case "Video":
        type = MediaType.Vid
        break
      default:
        type = MediaType.Unknown
        break
    }

    const album = response?.album ? Album.fromResponse(response.album) : undefined

    return new Media({
      id: response.eventMediaId,
      albumId: response.eventAlbumId,
      uploadedById: response.eventGuestId,
      uploadedBy: response.guestName,
      title: response.title,
      description: response.description,
      fileName: response.name,
      snapshotUrl: response.pathToSnapshot,
      thumbnailUrl: response.pathToThumb,
      fullSizeUrl: response.pathToFull,
      isPinned: response.isPinned,
      createdAt: response.dateCreated,
      downloadUrl: response.downloadURL,
      type,
      isPrivileged: response.isPrivileged,
      reactions: response.reactions,
      album,
      tags: response.tags,
      totalComments: response.commentsCount,
      requiresModeration: response.requiresModeration,
      isFlagged: response.flagged,
    })
  }

  public static fromResponse(response: IMediaResponse): Media {
    let type = MediaType.Unknown

    switch (response.Type) {
      case 0:
        type = MediaType.Pic
        break
      case 1:
        type = MediaType.Vid
        break
      default:
        type = MediaType.Unknown
        break
    }

    return new Media({
      id: response.EventMediaID,
      albumId: response.EventAlbumID,
      uploadedById: response.EventGuestID,
      uploadedBy: response.GuestName,
      title: response.Title,
      description: response.Description,
      fileName: response.Name,
      snapshotUrl: response.PathToSnapshot,
      thumbnailUrl: response.PathToThumb,
      fullSizeUrl: response.PathToFull,
      isPinned: response.IsPinned,
      createdAt: response.DateCreated,
      downloadUrl: response.DownloadURL,
      type,
      isPrivileged: response.IsPrivileged,
      reactions: response.Reactions,
      album: Album.fromResponse(response.Album),
      tags: response.Tags,
      totalComments: response.CommentsCount,
      requiresModeration: response.RequiresModeration,
      isFlagged: response.Flagged,
    })
  }
}
